import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import Switch from '../components/Switch'
import LoadingBar from '../components/LoadingBar'
import { Container } from '../components/Animations'
import Radom from '../state/Radom'
import { promiseToast } from '../util/Util'
import {
  SettingsCard,
  SettingsCardBody,
  SettingsHero,
  SettingsPage,
  SettingsRow,
  SettingsRowDescription,
  SettingsRows,
  SettingsRowTitle,
  SettingsSubtext,
  SettingsTitle,
  StatusPill
} from './Organization/SettingsLayout'

const NOTIFICATION_TYPES = [
  {
    name: 'Successful payments',
    id: 'PaymentSuccessful',
    description: 'Receive a notification when a payment completes successfully.'
  },
  {
    name: 'Incomplete payments',
    id: 'PaymentIncomplete',
    description: 'Receive a notification when an incomplete (i.e. partial amount of the total due) payment is made.'
  },
  {
    name: 'New subscriptions',
    id: 'Subscription',
    description: 'Receive a notification when a new subscription is created.'
  },
  {
    name: 'Subscription payment attempt failure',
    id: 'SubscriptionPaymentAttemptFailure',
    description: 'Receive a notification when an auto-pay enabled subscription payment fails.'
  },
  {
    name: 'Withdrawals',
    id: 'Withdrawal',
    description: 'Receive a notification when a withdrawal request completes.'
  }
]

const NotificationsSettings = observer((): React.ReactElement => {
  const [notifLoading, setNotifLoading] = useState(true)
  const [notificationSettings, setNotificationSettings] = useState<Record<string, boolean>>({})

  useEffect(() => {
    setNotifLoading(true)
    Radom.getNotificationSettings()
      .then(r => setNotificationSettings(r))
      .catch(err => { console.error('Failed to load notification settings', err) })
      .finally(() => setNotifLoading(false))
  }, [])

  const changeSetting = (setting: string): void => {
    const newSettings = { ...notificationSettings, [setting]: !notificationSettings[setting] }
    setNotificationSettings(newSettings)
    promiseToast(
      Radom.updateNotificationSettings({ settings: newSettings }).catch(err => {
        setNotificationSettings({ ...notificationSettings, [setting]: !!notificationSettings[setting] })
        throw err
      }),
      'Saving changes', 'Settings saved', 'Failed to update settings'
    )
  }

  const enabledCount = NOTIFICATION_TYPES.filter(n => notificationSettings[n.id]).length

  return (
    <Container>
      <SettingsPage>

        <SettingsHero>
          <div>
            <SettingsTitle>Notifications</SettingsTitle>
            <SettingsSubtext>
              Control which payment and account events send you email alerts.
            </SettingsSubtext>
          </div>
          <StatusPill $tone={enabledCount > 0 ? 'green' : 'neutral'}>
            {notifLoading ? 'Checking' : `${enabledCount} of ${NOTIFICATION_TYPES.length} enabled`}
          </StatusPill>
        </SettingsHero>

        <SettingsCard>
          <SettingsCardBody>
            <SettingsRows>
              {NOTIFICATION_TYPES.map(n =>
                <SettingsRow key={n.name}>
                  <div>
                    <SettingsRowTitle>{n.name}</SettingsRowTitle>
                    <SettingsRowDescription>{n.description}</SettingsRowDescription>
                  </div>
                  {notifLoading
                    ? <LoadingBar style={{ width: 42, borderRadius: 20 }} />
                    : <Switch
                      sizeScale={0.85}
                      checked={notificationSettings[n.id]}
                      onClick={() => changeSetting(n.id)}
                    />}
                </SettingsRow>
              )}
            </SettingsRows>
          </SettingsCardBody>
        </SettingsCard>

      </SettingsPage>
    </Container>
  )
})

export default NotificationsSettings
